import { useState, useCallback, useMemo, useContext } from 'react'
import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import { Formik, FormikProps } from 'formik'
import { useFetch } from 'hooks/useFetch'
import {
  getArbitrationMetadata,
  getArbitrationInfo,
  submitArbitration,
  addArbitrationDocuments
} from 'apis/arbitrationApi'
import {
  ArbitrationState,
  IssueTypes,
  IArbitrationInformationRequest,
  IArbitrationDocumentRequest,
  IGetArbitrationMetadataResponse,
  IGetArbitrationInfoResponse,
  arbitrationFormSchema,
  initialArbitrationValues,
  IArbitrationForm,
  IDocumentSubmit,
  IArbitrationDocumentInfo,
  IDropdownItem
} from 'types/arbitrationTypes'
import { ToastContext } from 'contexts/ToastContext'
import { SaleInfoSection } from './sections/SaleInfoSection'
import { ContactInfoSection } from './sections/ContactInfoSection'
import { ArbitrationRequestSection } from './sections/ArbitrationRequestSection'
import { ConfirmSubmitModal } from './sections/ConfirmSubmitModal'
import { StyledArbitrationForm, StyledArbitrationFormLoading } from './ArbitrationForm.styled'
import { useGlobalStore } from 'store/useGlobalStore'

const EMPTY_CONTACT_METHODS: IDropdownItem[] = []
const EMPTY_DOCUMENTS: IArbitrationDocumentInfo[] = []

interface IProps {
  vehicleInstanceID: number
  buyerVehiclePurchaseID: number
  onClose: () => void
  isUpdateAttachment: boolean
  onArbitrationComplete: (state: ArbitrationState) => void
  setIsPageLoading: (loading: boolean) => void
}

export const ArbitrationForm = ({
  vehicleInstanceID,
  buyerVehiclePurchaseID,
  onClose,
  isUpdateAttachment,
  onArbitrationComplete,
  setIsPageLoading
}: IProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [formValues, setFormValues] = useState<IArbitrationForm | null>(null)
  const [mileageWarning, setMileageWarning] = useState('')
  const { setToastData, setShowToast } = useContext(ToastContext)
  const getLocalText = useGlobalStore((state) => state.getLocalText)

  const { data: metadata, loading: metadataLoading } = useFetch<IGetArbitrationMetadataResponse>(
    () => getArbitrationMetadata(),
    []
  )

  const { data: detail, loading: detailLoading } = useFetch<IGetArbitrationInfoResponse>(
    () => getArbitrationInfo(vehicleInstanceID),
    [vehicleInstanceID]
  )

  const isLoading = metadataLoading || detailLoading

  const initialValues = useMemo((): IArbitrationForm => {
    if (!detail) return initialArbitrationValues

    const defaultContactMethodID = metadata?.ContactMethods?.[0]?.ID ?? 0

    return {
      ...initialArbitrationValues,
      name: detail.ContactInformation?.ContactName ?? '',
      email: detail.ContactInformation?.ContactEmail ?? '',
      phoneNumber: detail.ContactInformation?.PhoneNumber ?? '',
      contactMethodID: detail.ContactInformation?.ContactMethodID || defaultContactMethodID,
      issueTypeID: detail.ArbitrationInformation?.IssueTypeID ?? 0,
      resolutionTypeID: detail.ArbitrationInformation?.ResolutionTypeID ?? 0,
      currentMileage:
        (detail.ArbitrationInformation?.ArbitrationID ?? 0) > 0
          ? detail.ArbitrationInformation!.CurrentMileage.toString()
          : '',
      reasonForArbitration: detail.ArbitrationInformation?.Reason ?? ''
    }
  }, [detail, metadata])

  const handleMileageChange = (value: string) => {
    if (!metadata || !detail) return
    const mileage = Number(value)
    const saleMileage = detail.SaleInformation?.Mileage ?? 0
    const maxExceed = metadata.MaximumMileageExceed ?? 0

    if (!isNaN(mileage) && mileage >= 0 && mileage < saleMileage) {
      setMileageWarning(
        getLocalText(
          'ArbitrationMileageBelowWarning',
          'Please validate the mileage. Mileage entered is less than mileage at time of vehicle purchase.'
        )
      )
    } else if (!isNaN(mileage) && mileage > saleMileage + maxExceed) {
      setMileageWarning(
        getLocalText(
          'ArbitrationMileageExceedWarning',
          'Mileage exceeds allowable transportation mileage, arbitration may not be considered.'
        )
      )
    } else {
      setMileageWarning('')
    }
  }

  const handleFormSubmit = (values: IArbitrationForm) => {
    setFormValues(values)
    setHasAttemptedSubmit(true)

    const issueType = Number(values.issueTypeID)
    const resolutionType = Number(values.resolutionTypeID)

    if (!isUpdateAttachment && (issueType === 0 || resolutionType === 0)) {
      return
    }

    if (
      issueType === IssueTypes.PHYSICAL_DAMAGE &&
      values.documents.length < (metadata?.PhysicalDamageMinDocuments ?? 0) &&
      !isUpdateAttachment
    ) {
      return
    }

    if (
      issueType === IssueTypes.NON_PHYSICAL_DAMAGE &&
      values.documents.length < (metadata?.NonPhysicalDamageMinDocuments ?? 0) &&
      !isUpdateAttachment
    ) {
      return
    }

    if (isUpdateAttachment && values.documents.length === 0) {
      return
    }

    if (isUpdateAttachment) {
      setFormValues(values)
      handleAddAttachments(values)
      return
    }

    setShowConfirmModal(true)
  }

  const handleAddAttachments = useCallback(
    async (values: IArbitrationForm) => {
      if (!detail) return

      setIsSubmitting(true)
      setIsPageLoading(true)
      setShowToast(false)

      const documentRequest: IArbitrationDocumentRequest = {
        VehicleInstanceID: vehicleInstanceID,
        ArbitrationID: detail.ArbitrationInformation?.ArbitrationID ?? 0,
        SalesforceCaseID: detail.ArbitrationInformation?.SalesforceCaseID ?? '',
        VIN: detail.SaleInformation?.VIN ?? '',
        Documents: values.documents as IDocumentSubmit[]
      }

      try {
        const response = await addArbitrationDocuments(documentRequest)
        if (response.Success) {
          setToastData(true, 'Attachments added successfully.', 'infor')
        } else {
          setToastData(true, response.ErrorMessages?.[0] || 'Failed to add attachments.', 'error')
        }
      } catch {
        setToastData(true, 'An unexpected error occurred. Please try again.', 'error')
      } finally {
        onClose()
        setIsSubmitting(false)
        setIsPageLoading(false)
      }
    },
    [detail, vehicleInstanceID, onClose, setToastData, setShowToast]
  )

  const handleConfirmSubmit = useCallback(async () => {
    if (!formValues || !detail) return

    setShowConfirmModal(false)
    setIsSubmitting(true)
    setIsPageLoading(true)
    setShowToast(false)

    const request: IArbitrationInformationRequest = {
      VehicleInstanceID: vehicleInstanceID,
      BuyerVehiclePurchaseID: buyerVehiclePurchaseID,
      ContactName: formValues.name,
      ContactEmail: formValues.email,
      PhoneNumber: formValues.phoneNumber,
      ContactMethodID: Number(formValues.contactMethodID),
      IssueTypeID: Number(formValues.issueTypeID),
      ResolutionTypeID: Number(formValues.resolutionTypeID),
      CurrentMileage: Number(formValues.currentMileage),
      Reason: formValues.reasonForArbitration,
      Documents: formValues.documents as IDocumentSubmit[],
      VIN: detail.SaleInformation?.VIN ?? ''
    }

    try {
      const response = await submitArbitration(request)
      if (response.Success) {
        setToastData(true, 'Submit arbitration successfully.', 'infor')
      } else {
        setToastData(true, response.ErrorMessages?.[0] || 'Arbitration submission failed.', 'error')
      }

      if (response.Value?.State === ArbitrationState.SUBMITTED) {
        onArbitrationComplete(response.Value.State)
      }
    } catch {
      setToastData(true, 'An unexpected error occurred. Please try again.', 'error')
    } finally {
      onClose()
      setIsSubmitting(false)
      setIsPageLoading(false)
    }
  }, [
    formValues,
    detail,
    vehicleInstanceID,
    buyerVehiclePurchaseID,
    onArbitrationComplete,
    onClose,
    setToastData,
    setShowToast
  ])

  const renderFormContent = (formikProps: FormikProps<IArbitrationForm>) => (
    <Form onSubmit={formikProps.handleSubmit}>
      {detail?.SaleInformation && <SaleInfoSection sale={detail.SaleInformation} />}

      <ContactInfoSection
        values={formikProps.values}
        errors={formikProps.errors}
        touched={formikProps.touched}
        handleChange={formikProps.handleChange}
        contactMethods={metadata?.ContactMethods ?? EMPTY_CONTACT_METHODS}
        isUpdateAttachment={isUpdateAttachment}
        hasAttemptedSubmit={hasAttemptedSubmit}
      />

      {metadata && (
        <ArbitrationRequestSection
          values={formikProps.values}
          errors={formikProps.errors}
          touched={formikProps.touched}
          handleChange={formikProps.handleChange}
          setFieldValue={formikProps.setFieldValue}
          metadata={metadata}
          isUpdateAttachment={isUpdateAttachment}
          hasAttemptedSubmit={hasAttemptedSubmit}
          mileageWarning={mileageWarning}
          onMileageChange={handleMileageChange}
          existingDocuments={detail?.ArbitrationInformation?.Documents ?? EMPTY_DOCUMENTS}
        />
      )}

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={isSubmitting} onClick={() => setHasAttemptedSubmit(true)}>
          {isSubmitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="mr-2" />
              Submitting...
            </>
          ) : isUpdateAttachment ? (
            'Add Attachments'
          ) : (
            'Complete'
          )}
        </Button>
      </Modal.Footer>
    </Form>
  )

  const handleCancelConfirm = useCallback(() => setShowConfirmModal(false), [])

  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title>{isUpdateAttachment ? 'Add Attachments' : 'Arbitration Form'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <StyledArbitrationFormLoading>
            <Spinner animation="border" variant="primary" />
          </StyledArbitrationFormLoading>
        ) : (
          <StyledArbitrationForm>
            <Formik
              initialValues={initialValues}
              validationSchema={!isUpdateAttachment ? arbitrationFormSchema : undefined}
              onSubmit={handleFormSubmit}
              enableReinitialize
            >
              {renderFormContent}
            </Formik>
          </StyledArbitrationForm>
        )}
      </Modal.Body>

      <ConfirmSubmitModal
        show={showConfirmModal}
        isUpdateAttachment={isUpdateAttachment}
        issueTypeID={Number(formValues?.issueTypeID ?? 0)}
        physicalDamageMinDocuments={metadata?.PhysicalDamageMinDocuments ?? 0}
        nonPhysicalDamageMinDocuments={metadata?.NonPhysicalDamageMinDocuments ?? 0}
        onConfirm={handleConfirmSubmit}
        onCancel={handleCancelConfirm}
      />
    </>
  )
}
